/**
 * Meeting demo shell: configuration, join screen, and the scenario router.
 *
 * Scenes: `meeting` (existing conference demo), `1v1`, `live` and `voice`, selected with
 * `?scene=1v1|live|voice` (shareable, re-exported in every invite link). `?fake=1` (or the join
 * toggle) runs the whole demo on the SDK's deterministic FakeTransport so a host without the Docker
 * stack still gets a runnable demo; the online path is unchanged and always uses a real token.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createClient, type RtcClient } from '@nimeet/rtc-web';
import '@nimeet/ui-kit-react/ui-kit.css';
import { loadConfig, normalizeChannel, parseInvite } from './config';
import {
  HttpImProvider,
  LocalImMeetingBridge,
  MockImProvider,
  deriveUid,
  type RtcTokenInfo,
} from './im/providers';
import {
  MeetingSession,
  SceneCapacityError,
  describeError,
  type MeetingClient,
  type MeetingRole,
} from './meeting/session';
import { installE2EProbe } from './meeting/e2e-probe';
import { I18nProvider, LanguageSwitcher, useI18n } from './i18n';
import type { MessageKey } from './i18n/messages';
import { MeetingThemeProvider, ThemeToggle } from './theme';
import {
  SCENES,
  SCENE_ORDER,
  sceneDescriptionKey,
  sceneFromSearch,
  sceneNameKey,
  sceneTitleKey,
  validateSceneJoin,
  withScene,
  type SceneId,
} from './scenarios/scenes';
import { OfflineDemo } from './scenarios/offlineDemo';
import { Banner, ToastHost, useToast } from './ui';
import { MeetingRoom } from './rooms/MeetingRoom';
import { CallRoom } from './rooms/CallRoom';
import { LiveRoom } from './rooms/LiveRoom';
import { VoiceRoom } from './rooms/VoiceRoom';
import type { RoomContext } from './rooms/types';
import './styles.css';

const CAMERA_KEY = 'nimeet.camera.deviceId';
const MICROPHONE_KEY = 'nimeet.microphone.deviceId';
const OFFLINE_KEY = 'nimeet.meeting.offline';

/** Virtual/remote cameras (ToDesk, OBS, TranScreen, ...) are frequently held by another app. */
function isVirtualDevice(label: string): boolean {
  return /virtual|tran(?:s)?screen|todesk|obs|snap|manycam|droidcam|screen capture|远程|虚拟/i.test(
    label,
  );
}

export function App() {
  return (
    <I18nProvider>
      <MeetingThemeProvider>
        <AppShell />
      </MeetingThemeProvider>
    </I18nProvider>
  );
}

function AppShell() {
  const { t } = useI18n();
  const config = useMemo(
    () => loadConfig(import.meta.env as Record<string, string | undefined>),
    [],
  );
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const initialChannel = useMemo(() => parseInvite(search ? `?${search.slice(1)}` : ''), [search]);
  const [scene, setScene] = useState<SceneId>(() => sceneFromSearch(search));
  const autoInstant = useMemo(
    () => new URLSearchParams(search).get('instant') === '1',
    [search],
  );
  const [offline, setOffline] = useState<boolean>(() => {
    const params = new URLSearchParams(search);
    if (params.get('fake') === '1') return true;
    if (params.get('fake') === '0') return false;
    try {
      return window.localStorage.getItem(OFFLINE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const { toast, show } = useToast();

  const [screen, setScreen] = useState<'join' | 'room'>('join');
  const [context, setContext] = useState<RoomContext | null>(null);
  const [joinErrorKey, setJoinErrorKey] = useState<MessageKey | null>(null);
  const [joinErrorText, setJoinErrorText] = useState<string | null>(null);
  const demoRef = useRef<OfflineDemo | null>(null);

  const changeScene = useCallback((next: SceneId) => {
    setScene(next);
    if (typeof window === 'undefined') return;
    try {
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${withScene(window.location.search, next)}`,
      );
    } catch {
      // history is best effort
    }
  }, []);

  const changeOffline = useCallback((next: boolean) => {
    setOffline(next);
    try {
      window.localStorage.setItem(OFFLINE_KEY, next ? '1' : '0');
      const params = new URLSearchParams(window.location.search);
      if (next) params.set('fake', '1');
      else params.delete('fake');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}`,
      );
    } catch {
      // storage/history are best effort
    }
  }, []);

  const start = useCallback(
    async (input: {
      channel: string;
      displayName: string;
      role: MeetingRole;
      publishAudio: boolean;
      publishVideo: boolean;
      videoDeviceId?: string;
      audioDeviceId?: string;
    }) => {
      setJoinErrorKey(null);
      setJoinErrorText(null);
      const validation = validateSceneJoin(scene, input.role);
      if (!validation.ok) {
        setJoinErrorKey(validation.errorKey);
        return;
      }
      const spec = SCENES[scene];
      const publishAudio = spec.audioOnly ? true : input.publishAudio;
      const publishVideo = spec.audioOnly || input.role === 'audience' ? false : input.publishVideo;
      const joinUid = deriveUid(`${input.channel}:${input.displayName}`);
      // Voice listeners stay unpublished but get a publish-capable token so host approval can put
      // them on a mic seat. Live audience deliberately keeps the subscriber-only token.
      const tokenRole: MeetingRole =
        scene === 'voice' && input.role === 'audience' ? 'host' : input.role;

      if (offline) {
        const demo = new OfflineDemo({
          scene,
          localRole: input.role,
          localUid: joinUid,
          localName: input.displayName,
        });
        demoRef.current = demo;
        const session = new MeetingSession(demo.client as unknown as MeetingClient, { t });
        const bridge = new LocalImMeetingBridge();
        bridge.addMember(input.channel, {
          userId: `self-${input.displayName}`,
          displayName: input.displayName,
        });
        const room: RoomContext = {
          session,
          client: demo.client,
          provider: new MockImProvider({ appId: 'demo-app-id' }),
          bridge,
          scene,
          offline: true,
          role: input.role,
          displayName: input.displayName,
          channel: input.channel,
          onLeave: () => {
            demoRef.current?.dispose();
            demoRef.current = null;
            setScreen('join');
            setContext(null);
          },
          notify: show,
        };
        setContext(room);
        setScreen('room');
        session.openChat(false);
        try {
          await session.join({
            channel: input.channel,
            displayName: input.displayName,
            role: input.role,
            tokenRole,
            uid: joinUid,
            publishAudio,
            publishVideo,
            scene,
            maxParticipants: spec.maxParticipants,
          });
          demo.start();
        } catch (error) {
          demoRef.current?.dispose();
          demoRef.current = null;
          setScreen('join');
          setContext(null);
          if (error instanceof SceneCapacityError) setJoinErrorKey(error.messageKey);
          else setJoinErrorText(describeError(error));
        }
        return;
      }

      if (!config.im.tokenEndpoint) {
        setJoinErrorKey('join.error.tokenEndpoint');
        return;
      }
      const provider = new HttpImProvider({
        tokenEndpoint: config.im.tokenEndpoint,
        uidEndpoint: config.im.uidEndpoint,
      });
      // Resolve one token before building the client: the control plane's seeded appId is dynamic
      // (in-memory store) and rtc-web refuses a client whose appId differs from the token's. The
      // prefetch only teaches us that appId; session.join still fetches a fresh token through the
      // same provider.
      let prefetched: RtcTokenInfo;
      try {
        prefetched = await provider.getRTCTokenInfo({
          channelName: input.channel,
          uid: joinUid,
          role: tokenRole,
        });
      } catch (error) {
        setJoinErrorText(
          t('join.error.token', {
            endpoint: config.im.tokenEndpoint,
            reason: describeError(error),
          }),
        );
        return;
      }
      const client = createClient({
        appId: prefetched.appId,
        tokenProvider: {
          type: 'callback',
          getRTCTokenInfo: async (request) => {
            const info = await provider.getRTCTokenInfo({
              channelName: request.channelName,
              uid: request.uid,
              role: request.role,
            });
            // Same-origin `/livekit` keeps HTTP localhost and HTTPS deployments on one build and
            // avoids mixed-content blocking when the page is served over TLS.
            const serverUrl = config.sfuUrl ?? info.serverUrl;
            if (!serverUrl)
              throw new Error(
                'no media URL: set VITE_SFU_URL or return serverUrl from getRTCTokenInfo',
              );
            return { appId: info.appId, rtcToken: info.rtcToken, rtcUid: info.rtcUid, serverUrl };
          },
        },
        transport: 'auto',
        logLevel: 'warn',
        autoRenew: true,
        iceServers: config.iceServers,
        ...(config.sfuUrl ? { serverUrl: config.sfuUrl } : {}),
        ...(input.videoDeviceId ? { videoDeviceId: input.videoDeviceId } : {}),
        ...(input.audioDeviceId ? { audioDeviceId: input.audioDeviceId } : {}),
      });
      const session = new MeetingSession(client as unknown as MeetingClient, { t });
      // Additive E2E instrumentation (only active with `?e2e=1`); never changes app behaviour.
      installE2EProbe({
        client,
        session,
        scene,
        channel: input.channel,
        displayName: input.displayName,
        role: input.role,
        offline: false,
      });
      const bridge = new LocalImMeetingBridge();
      bridge.addMember(input.channel, {
        userId: `self-${input.displayName}`,
        displayName: input.displayName,
      });
      const room: RoomContext = {
        session,
        client: client as unknown as RtcClient,
        provider,
        bridge,
        scene,
        offline: false,
        role: input.role,
        displayName: input.displayName,
        channel: input.channel,
        onLeave: () => {
          setScreen('join');
          setContext(null);
        },
        notify: show,
      };
      setContext(room);
      setScreen('room');
      try {
        await session.join({
          channel: input.channel,
          displayName: input.displayName,
          role: input.role,
          tokenRole,
          uid: joinUid,
          publishAudio,
          publishVideo,
          scene,
          maxParticipants: spec.maxParticipants,
        });
      } catch (error) {
        setScreen('join');
        setContext(null);
        if (error instanceof SceneCapacityError) setJoinErrorKey(error.messageKey);
        else setJoinErrorText(describeError(error));
      }
    },
    [config, offline, scene, show, t],
  );

  const leaveRoom = useCallback(async () => {
    if (context) await context.session.leave().catch(() => undefined);
    demoRef.current?.dispose();
    demoRef.current = null;
    setScreen('join');
    setContext(null);
  }, [context]);

  useEffect(
    () => () => {
      demoRef.current?.dispose();
      demoRef.current = null;
    },
    [],
  );

  return (
    <div className="nm-app" data-theme={offline ? 'offline' : 'live'}>
      <header className="nm-topbar">
        <div className="nm-brand">
          <span className="nm-brand-dot" />
          <span data-testid="brand">{t('app.brand', { brand: config.brand })}</span>
          <span
            className={`nm-badge ${offline ? 'is-muted' : 'is-live'}`}
            data-testid="connection-mode"
          >
            {offline ? t('app.offlineTag') : t('app.tagline')}
          </span>
        </div>
        <div className="nm-row">
          {screen === 'join' || !context ? (
            <>
              <LanguageSwitcher />
              <ThemeToggle />
            </>
          ) : (
            <span className="nm-sub" data-testid="room-mode">
              {offline ? t('app.offlineTag') : t('app.tagline')}
            </span>
          )}
        </div>
      </header>
      {screen === 'join' || !context ? (
        <JoinScreen
          scene={scene}
          offline={offline}
          config={config}
          defaultChannel={initialChannel.channel ?? ''}
          defaultName={initialChannel.from ?? ''}
          autoInstant={autoInstant}
          errorKey={joinErrorKey}
          errorText={joinErrorText}
          onScene={changeScene}
          onOffline={changeOffline}
          onJoin={start}
        />
      ) : (
        <RoomRouter
          context={context}
          onLeave={() => {
            void leaveRoom();
          }}
        />
      )}
      <ToastHost message={toast} />
    </div>
  );
}

function RoomRouter({ context, onLeave }: { context: RoomContext; onLeave: () => void }) {
  const room = { ...context, onLeave };
  switch (context.scene) {
    case '1v1':
      return <CallRoom context={room} />;
    case 'live':
      return <LiveRoom context={room} />;
    case 'voice':
      return <VoiceRoom context={room} />;
    default:
      return <MeetingRoom context={room} />;
  }
}

function JoinScreen({
  scene,
  offline,
  config,
  defaultChannel,
  defaultName,
  autoInstant,
  errorKey,
  errorText,
  onScene,
  onOffline,
  onJoin,
}: {
  scene: SceneId;
  offline: boolean;
  config: ReturnType<typeof loadConfig>;
  defaultChannel: string;
  defaultName: string;
  /** ?instant=1：落地页自动建会进入（官网「体验 RTC」直链用）。 */
  autoInstant?: boolean;
  errorKey: MessageKey | null;
  errorText: string | null;
  onScene: (scene: SceneId) => void;
  onOffline: (offline: boolean) => void;
  onJoin: (input: {
    channel: string;
    displayName: string;
    role: MeetingRole;
    publishAudio: boolean;
    publishVideo: boolean;
    videoDeviceId?: string;
    audioDeviceId?: string;
  }) => void;
}) {
  const { t } = useI18n();
  const spec = SCENES[scene];
  const [channel, setChannel] = useState(defaultChannel);
  const [name, setName] = useState(defaultName);
  const [role, setRole] = useState<MeetingRole>(
    spec.allowedRoles.includes('host') ? 'host' : 'audience',
  );
  const [audio, setAudio] = useState(spec.audioOnly);
  const [video, setVideo] = useState(!spec.audioOnly);
  const previewRef = useRef<HTMLVideoElement | null>(null);
  const [previewErrorKey, setPreviewErrorKey] = useState<MessageKey | null>(null);
  const [previewErrorText, setPreviewErrorText] = useState<string | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState(() => localStorage.getItem(CAMERA_KEY) ?? '');
  const [microphoneId, setMicrophoneId] = useState(
    () => localStorage.getItem(MICROPHONE_KEY) ?? '',
  );
  const [touched, setTouched] = useState(false);

  // Scenario switches reset publish toggles and the role to the scene's allowed set.
  useEffect(() => {
    setVideo(!SCENES[scene].audioOnly);
    setAudio(SCENES[scene].audioOnly ? true : audio);
    if (!SCENES[scene].allowedRoles.includes(role))
      setRole(SCENES[scene].allowedRoles[0] ?? 'audience');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  const wantVideo = !spec.audioOnly && role !== 'audience' && video;
  const wantAudio = role !== 'audience' ? true : spec.audioOnly ? false : audio;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    void (async () => {
      if (!wantVideo || !navigator.mediaDevices?.getUserMedia) {
        if (!navigator.mediaDevices?.getUserMedia) setPreviewErrorKey('join.preview.unsupported');
        return;
      }
      // The Windows default camera is often a virtual camera that is already held by another app
      // (NotReadableError: Device in use). Enumerate and try explicit deviceIds instead of trusting
      // `{ video: true }`.
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      const cameraList = devices.filter((device) => device.kind === 'videoinput');
      const microphoneList = devices.filter((device) => device.kind === 'audioinput');
      setCameras(cameraList);
      setMicrophones(microphoneList);
      const preferred =
        cameraId ||
        cameraList.find((device) => !isVirtualDevice(device.label))?.deviceId ||
        cameraList[0]?.deviceId ||
        '';
      const candidates = [
        ...new Set([preferred, ...cameraList.map((device) => device.deviceId)].filter(Boolean)),
      ];
      let lastError: unknown = null;
      for (const deviceId of candidates.length > 0 ? candidates : ['']) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: deviceId ? { deviceId: { exact: deviceId } } : true,
            audio: false,
          });
          if (cancelled) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          const actualId = stream.getVideoTracks()[0]?.getSettings().deviceId ?? deviceId;
          localStorage.setItem(CAMERA_KEY, actualId);
          if (actualId && actualId !== cameraId) setCameraId(actualId);
          if (previewRef.current) previewRef.current.srcObject = stream;
          setPreviewErrorKey(null);
          setPreviewErrorText(null);
          return;
        } catch (error) {
          lastError = error;
          stream?.getTracks().forEach((track) => track.stop());
          stream = null;
        }
      }
      const reason =
        lastError instanceof Error
          ? `${lastError.name}: ${lastError.message}`
          : t('common.unknown');
      setPreviewErrorText(t('join.preview.unavailable', { reason }));
    })();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantVideo, cameraId]);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    let cancelled = false;
    void navigator.mediaDevices.enumerateDevices().then((devices) => {
      if (!cancelled) setMicrophones(devices.filter((device) => device.kind === 'audioinput'));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!microphoneId) {
      const preferred =
        microphones.find((device) => !isVirtualDevice(device.label))?.deviceId ??
        microphones[0]?.deviceId;
      if (preferred) setMicrophoneId(preferred);
    }
  }, [microphones, microphoneId]);

  const channelTrimmed = channel.trim();
  const nameTrimmed = name.trim();
  const channelError: MessageKey | null =
    touched && channelTrimmed.length === 0 ? 'join.error.channelRequired' : null;
  const nameError: MessageKey | null =
    touched && nameTrimmed.length === 0 ? 'join.error.nameRequired' : null;
  const valid = channelTrimmed.length > 0 && nameTrimmed.length > 0;
  const roleDenied = !spec.allowedRoles.includes(role);

  // 一键开会：随机会议号 + 默认昵称，以场景允许的最高权限身份进入
  const startInstant = useCallback(() => {
    const code = `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
    const nextChannel = normalizeChannel(code) ?? code;
    const nextName = nameTrimmed || t('join.guestName');
    const nextRole: MeetingRole = spec.allowedRoles.includes('host')
      ? 'host'
      : (spec.allowedRoles[0] ?? 'audience');
    setChannel(nextChannel);
    setName(nextName);
    onJoin({
      channel: nextChannel,
      displayName: nextName,
      role: nextRole,
      publishAudio: wantAudio,
      publishVideo: !spec.audioOnly && nextRole !== 'audience' && video,
      videoDeviceId: cameraId || undefined,
      audioDeviceId: microphoneId || undefined,
    });
  }, [
    nameTrimmed,
    spec.allowedRoles,
    spec.audioOnly,
    onJoin,
    wantAudio,
    video,
    cameraId,
    microphoneId,
    t,
  ]);

  // ?instant=1：首页直链自动开会（只触发一次）
  const autoTriggered = useRef(false);
  useEffect(() => {
    if (!autoInstant || autoTriggered.current) return;
    autoTriggered.current = true;
    const timer = window.setTimeout(() => startInstant(), 60);
    return () => window.clearTimeout(timer);
  }, [autoInstant, startInstant]);
  const error = errorKey ? t(errorKey) : errorText;

  return (
    <main className="nm-join" data-testid="join-screen" data-scene={scene}>
      <section>
        <div className="nm-sub">{t('join.subtitle')}</div>
        <h1>{t('join.title')}</h1>
        <p className="lede">{offline ? t('join.ledeFake') : t('join.lede')}</p>
        {offline ? (
          <Banner tone="warn">
            <span data-testid="offline-banner">{t('join.demo.hint')}</span>
          </Banner>
        ) : null}
        {!window.isSecureContext ? <Banner tone="warn">{t('join.insecure')}</Banner> : null}
        {error ? (
          <Banner tone="error">
            <span data-testid="join-error">{error}</span>
          </Banner>
        ) : null}

        <div
          className="nm-scene-picker"
          role="radiogroup"
          aria-label={t('scene.label')}
          data-testid="scene-picker"
        >
          {SCENE_ORDER.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={scene === id}
              className={`nm-scene-card${scene === id ? ' is-active' : ''}`}
              data-testid={`scene-option-${id}`}
              onClick={() => onScene(id)}
            >
              <span className="nm-scene-card__title">{t(sceneNameKey(id))}</span>
              <span className="nm-scene-card__desc">{t(sceneDescriptionKey(id))}</span>
            </button>
          ))}
        </div>
        <p className="nm-sub" data-testid="scene-hint">
          {t('scene.hint')}
        </p>

        {(() => null)()}
        <div style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="nm-btn is-active"
            data-testid="start-instant"
            onClick={startInstant}
            style={{ minWidth: 160 }}
          >
            {t('join.instant')}
          </button>
          <span className="nm-sub" style={{ margin: 0 }}>
            {t('join.instantHint')}
          </span>
        </div>

        <form
          style={{ marginTop: 18 }}
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (!valid || roleDenied) return;
            onJoin({
              channel: channelTrimmed,
              displayName: nameTrimmed,
              role,
              publishAudio: wantAudio,
              publishVideo: wantVideo,
              videoDeviceId: cameraId || undefined,
              audioDeviceId: microphoneId || undefined,
            });
          }}
        >
          <div className="nm-field">
            <label htmlFor="join-channel">{t('join.field.channel')}</label>
            <input
              id="join-channel"
              className="nm-input"
              value={channel}
              data-testid="join-channel"
              onChange={(event) => setChannel(event.target.value)}
              placeholder={t('join.placeholder.channel')}
              autoFocus
            />
            {channelError ? <span className="nm-field-error">{t(channelError)}</span> : null}
          </div>
          <div className="nm-field">
            <label htmlFor="join-name">{t('join.field.name')}</label>
            <input
              id="join-name"
              className="nm-input"
              value={name}
              data-testid="join-name"
              onChange={(event) => setName(event.target.value)}
              placeholder={t('join.placeholder.name')}
            />
            {nameError ? <span className="nm-field-error">{t(nameError)}</span> : null}
          </div>
          <div className="nm-row" style={{ marginBottom: 14 }}>
            <button
              type="button"
              className="nm-toggle"
              aria-pressed={role !== 'audience'}
              data-testid="join-mic"
              onClick={() => setAudio(!audio)}
              disabled={role === 'audience'}
            >
              {audio ? t('join.mic.on') : t('join.mic.off')}
            </button>
            <button
              type="button"
              className="nm-toggle"
              aria-pressed={wantVideo}
              data-testid="join-camera"
              onClick={() => setVideo(!video)}
              disabled={spec.audioOnly || role === 'audience'}
            >
              {wantVideo ? t('join.camera.on') : t('join.camera.off')}
            </button>
            <label className="nm-field" style={{ margin: 0, minWidth: 150 }}>
              <span>{t('join.field.role')}</span>
              <select
                className="nm-select"
                value={role}
                data-testid="join-role"
                onChange={(event) => setRole(event.target.value as MeetingRole)}
              >
                {spec.allowedRoles.includes('host') ? (
                  <option value="host">{t('join.role.host')}</option>
                ) : null}
                {spec.allowedRoles.includes('audience') ? (
                  <option value="audience">
                    {spec.audioOnly ? t('join.role.listener') : t('join.role.audience')}
                  </option>
                ) : null}
              </select>
            </label>
            <button
              type="button"
              className="nm-toggle"
              aria-pressed={offline}
              data-testid="join-demo"
              onClick={() => onOffline(!offline)}
            >
              {t('join.demo.toggle')}
            </button>
          </div>
          {roleDenied ? (
            <Banner tone="warn">
              {t('join.error.roleDenied', { scene: t(sceneNameKey(scene)) })}
            </Banner>
          ) : null}
          <div className="nm-row" style={{ marginBottom: 14, alignItems: 'flex-end' }}>
            <label className="nm-field" style={{ margin: 0, flex: 1 }}>
              <span>{t('join.field.camera')}</span>
              <select
                className="nm-select"
                value={cameraId}
                data-testid="join-camera-select"
                onChange={(event) => {
                  const id = event.target.value;
                  setCameraId(id);
                  localStorage.setItem(CAMERA_KEY, id);
                }}
              >
                {cameras.length === 0 ? (
                  <option value="">{t('join.device.defaultCamera')}</option>
                ) : null}
                {cameras.map((device, index) => (
                  <option key={device.deviceId || index} value={device.deviceId}>
                    {device.label || t('join.device.cameraN', { index: index + 1 })}
                  </option>
                ))}
              </select>
            </label>
            <label className="nm-field" style={{ margin: 0, flex: 1 }}>
              <span>{t('join.field.microphone')}</span>
              <select
                className="nm-select"
                value={microphoneId}
                data-testid="join-microphone-select"
                onChange={(event) => {
                  const id = event.target.value;
                  setMicrophoneId(id);
                  localStorage.setItem(MICROPHONE_KEY, id);
                }}
              >
                {microphones.length === 0 ? (
                  <option value="">{t('join.device.defaultMicrophone')}</option>
                ) : null}
                {microphones.map((device, index) => (
                  <option key={device.deviceId || index} value={device.deviceId}>
                    {device.label || t('join.device.microphoneN', { index: index + 1 })}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            className="nm-btn is-active"
            type="submit"
            disabled={!valid || roleDenied}
            style={{ minWidth: 160 }}
            data-testid="join-submit"
          >
            {t('join.submit')}
          </button>
          <p className="nm-sub" style={{ marginTop: 10 }}>
            {config.im.tokenEndpoint
              ? `${t('meeting.settings.mode')}: ${config.im.tokenEndpoint}`
              : t('join.note.fake')}
          </p>
        </form>
      </section>
      <aside>
        <div className="nm-preview" data-testid="join-preview">
          {wantVideo ? <video ref={previewRef} autoPlay playsInline muted /> : null}
          {!wantVideo || previewErrorKey || previewErrorText ? (
            <div className="nm-placeholder">
              {previewErrorText ?? (previewErrorKey ? t(previewErrorKey) : t('join.preview.off'))}
            </div>
          ) : null}
        </div>
        <div className="nm-mock-note">{offline ? t('join.note.fake') : t('join.note.online')}</div>
        <div className="nm-scene-summary" data-testid="scene-summary">
          <strong>{t(sceneTitleKey(scene))}</strong>
          <p>{t(sceneDescriptionKey(scene))}</p>
          {spec.seats ? <p>{t('voice.seats', { occupied: 0, total: spec.seats })}</p> : null}
          {spec.maxParticipants ? (
            <p>
              {t('1v1.title')} · {spec.maxParticipants}
            </p>
          ) : null}
        </div>
      </aside>
    </main>
  );
}
